import type { NavigateFunction } from 'react-router-dom';
import { buildBlobProxyUrl } from './blobProxyUrl';

export type WorkflowType = 
  | 'question' 
  | 'comparison' 
  | 'summarize' 
  | 'conversion' 
  | 'dataanalysis' 
  | 'aicomparison' 
  | 'companystandard'
  | 'documentcontradiction';

interface WorkflowRouteMap {
  [key: string]: {
    path: string;
    storageKey?: string;
  };
}

export const workflowRoutes: WorkflowRouteMap = {
  conversion: {
    path: '/conversion-results',
    storageKey: process.env.REACT_APP_CONVERSION_RESULT_STORAGE_KEY || 'conversionResult',
  },
  question: {
    path: '/document-analysis',
    storageKey: process.env.REACT_APP_ANALYSIS_RESULT_STORAGE_KEY || 'documentAnalysisResult',
  },
  comparison: {
    path: '/specs-comparison',
    storageKey: process.env.REACT_APP_COMPARISON_RESULT_STORAGE_KEY || 'specsComparisonResult',
  },
  summarize: {
    path: '/document-analysis',
    storageKey: process.env.REACT_APP_ANALYSIS_RESULT_STORAGE_KEY || 'documentAnalysisResult',
  },
  dataanalysis: {
    path: '/document-analysis',
    storageKey: process.env.REACT_APP_ANALYSIS_RESULT_STORAGE_KEY || 'documentAnalysisResult',
  },
  aicomparison: {
    path: '/specs-comparison',
    storageKey: process.env.REACT_APP_COMPARISON_RESULT_STORAGE_KEY || 'specsComparisonResult',
  },
  companystandard: {
    path: '/company-standard-results',
    storageKey: process.env.REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY || 'companyStandardDocuments',
  },
  documentcontradiction: {
    path: '/document-contradiction',
    storageKey: process.env.REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY || 'contradictionResult',
  },
  technischeschatting: {
    path: '/technische-schatting',
    storageKey: 'technischeSchattingResult',
  },
  // Alias for entries created before the workflow key was normalised to lowercase
  technicalestimate: {
    path: '/technische-schatting',
    storageKey: 'technischeSchattingResult',
  },
};

/**
 * Unified workflow history response interface
 * Backend should return this format for all workflow types
 */
export interface UnifiedWorkflowHistoryResponse {
  _id: string | { $oid: string };
  title: string;
  userId: string;
  companyId: string;
  workflow: string;
  workflowType: string;
  instruction?: string;
  timestamp: Date | { $date: string };
  
  // Unified file handling - all workflows should provide this
  files: Array<{
    fileName: string;
    fileUrl: string;
    fileType?: 'source' | 'result' | 'word' | 'pdf' | 'converted';
  }>;
  
  // Original response preserved for workflow-specific data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any;
}

/**
 * Transform workflow data to match the expected format for each workflow page
 * Handles both unified format and legacy formats for backward compatibility
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformWorkflowData = (workflow: string, apiData: any): any => {
  // Check if this is the new unified format
  const isUnifiedFormat = apiData.files && Array.isArray(apiData.files);
  
  if (isUnifiedFormat) {
    // New unified format - extract common fields
    const documentId = typeof apiData._id === 'string' ? apiData._id : apiData._id?.$oid;
    const fileName = apiData.files[0]?.fileName || apiData.title || 'Document';
    const documentUrl = apiData.files[0]?.fileUrl;
    
    switch (workflow.toLowerCase()) {
      case 'conversion': {
        // Source file is in files array (original file)
        const sourceFile = apiData.files[0];
        // Converted file is in response (result of conversion)
        const convertedFileUrl = apiData.response?.convertedFileUrl || apiData.response?.url;
        const convertedFileName = apiData.response?.convertedFileName || apiData.response?.fileName || apiData.title;
        
        return {
          documentId,
          fileName: sourceFile?.fileName || apiData.title,
          conversionType: apiData.workflowType, // e.g., "WordToPdf" or "PdfToWord"
          conversionResults: {
            convertedFileName: convertedFileName,
            convertedFileUrl: convertedFileUrl,
          },
        };
      }
      
      case 'question':
      case 'summarize':
      case 'dataanalysis':
        return {
          documentId,
          fileName,
          documentUrl,
          analysisType: apiData.workflowType || workflow,
          customInstructions: apiData.instruction || '',
          isExistingDocument: true,
          analysisResults: {
            success: true,
            data: apiData.response,
            message: 'Analysis results loaded from history',
          },
        };
      
      case 'comparison':
      case 'aicomparison': {
        // Comparison response contains word and pdf URLs in response object
        const wordUrl = apiData.response?.word || apiData.response?.wordUrl;
        const pdfUrl = apiData.response?.pdf || apiData.response?.pdfUrl;
        
        return {
          requestId: documentId,
          comparisonName: apiData.title,
          comparisonType: apiData.workflowType,
          documentUrls: {
            word: wordUrl,
            pdf: pdfUrl,
          },
          response: {
            success: true,
            data: {
              word: wordUrl,
              pdf: pdfUrl,
            },
            message: 'Comparison results loaded from history',
          },
        };
      }
      
      case 'companystandard':
        return {
          documentId,
          fileName,
          analysisResults: {
            success: true,
            data: apiData.response,
            message: 'Company standard results loaded from history',
          },
        };
      
      case 'documentcontradiction': {
        // Extract PDF URLs from response for document contradiction
        const referenceDocumentUrl = buildBlobProxyUrl(apiData.files.find((f: any) => f.fileType === 'reference')?.fileUrl || apiData.files[0]?.fileUrl);
        const targetDocumentUrl = buildBlobProxyUrl(apiData.files.find((f: any) => f.fileType === 'target')?.fileUrl || apiData.files[1]?.fileUrl);

        return {
          documentId,
          fileName: apiData.title,
          contradictionName: apiData.title,
          analysisResults: apiData.response,
          referenceDocumentUrl,
          targetDocumentUrl,
          timestamp: apiData.timestamp,
        };
      }

      case 'technischeschatting':
      case 'technicalestimate':
        return {
          id: documentId,
          jobId: apiData.jobId,
          title: apiData.title,
        };

      default:
        return apiData;
    }
  }
  
  // Legacy format handling (for backward compatibility with existing API responses)
  const documentData = apiData.data;
  const workflowData = apiData.data?.data || apiData.data;
  
  switch (workflow.toLowerCase()) {
    case 'conversion':
      return {
        documentId: documentData?.id,
        fileName: documentData?.tittle,
        conversionType: documentData?.typeOfDocument,
        conversionResults: {
          convertedFileName: workflowData?.convertedFileName || documentData?.tittle,
          convertedFileUrl: workflowData?.convertedFileUrl || documentData?.documentLink,
        },
      };
    
    case 'question':
    case 'summarize':
    case 'dataanalysis': {
      const fileName = workflowData?.output?.file_name ||
                      workflowData?.fileName || 
                      workflowData?.file_name || 
                      documentData?.tittle || 
                      'Document';
      
      return {
        documentId: documentData?.id,
        fileName,
        documentUrl: documentData?.documentLink,
        analysisType: workflow,
        customInstructions: documentData?.question || documentData?.description || '',
        isExistingDocument: true,
        analysisResults: apiData,
      };
    }
    
    case 'comparison':
    case 'aicomparison':
      return {
        requestId: documentData?.id,
        comparisonName: documentData?.tittle,
        comparisonType: documentData?.typeOfDocument,
        documentUrls: workflowData?.documentUrls || {
          word: workflowData?.word,
          pdf: workflowData?.pdf,
        },
        response: apiData,
      };
    
    case 'companystandard':
      return {
        documentId: documentData?.id,
        fileName: documentData?.tittle,
        analysisResults: apiData,
      };
    
    case 'documentcontradiction': {
      // Legacy format for document contradiction
      const referenceDocumentUrl = buildBlobProxyUrl(workflowData?.referenceDocumentUrl || documentData?.documentLink);
      const targetDocumentUrl = buildBlobProxyUrl(workflowData?.targetDocumentUrl);

      return {
        documentId: documentData?.id,
        fileName: documentData?.tittle,
        contradictionName: documentData?.tittle,
        analysisResults: apiData,
        referenceDocumentUrl,
        targetDocumentUrl,
      };
    }

    case 'technischeschatting':
    case 'technicalestimate':
      return {
        id: documentData?.id || apiData?._id,
        jobId: apiData?.jobId,
        title: apiData?.title,
      };

    default:
      return apiData;
  }
};

/**
 * Navigate to the appropriate workflow page based on workflow type
 * Stores data in sessionStorage as backup and passes via navigation state
 */
export const navigateToWorkflow = (
  workflow: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflowData: any,
  navigate: NavigateFunction,
  // Extra keys merged into the navigation state only (not persisted to sessionStorage) —
  // e.g. { fromResultsPage: true } so the target page can tell it was opened from the
  // Resultaten history page specifically, as opposed to an in-page history drawer.
  extraState?: Record<string, unknown>
): void => {
  const route = workflowRoutes[workflow.toLowerCase()];

  if (!route) {
    console.error(`Unknown workflow type: ${workflow}`);
    return;
  }

  const transformedData = transformWorkflowData(workflow, workflowData);

  // Clear old sessionStorage data first, then write new data
  if (route.storageKey) {
    try {
      sessionStorage.removeItem(route.storageKey); // Clear old data
      sessionStorage.setItem(route.storageKey, JSON.stringify(transformedData));
    } catch (error) {
      console.error('Error storing workflow data in session storage:', error);
    }
  }

  // Navigate with state (preferred method - this should take priority)
  navigate(route.path, { state: { ...transformedData, ...extraState }, replace: true });
};

/**
 * Get the route path for a workflow type
 */
export const getWorkflowRoute = (workflow: string): string | null => {
  const route = workflowRoutes[workflow.toLowerCase()];
  return route?.path || null;
};

/**
 * Get the storage key for a workflow type
 */
export const getWorkflowStorageKey = (workflow: string): string | null => {
  const route = workflowRoutes[workflow.toLowerCase()];
  return route?.storageKey || null;
};
